import { useContext, useLayoutEffect, useRef, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { TemplateType } from '@/types/boot';
import type { PollingStatus } from '@/types/order';
import { getCheckoutTemplate } from './TemplateRegistry';
import { consumeSuppressNextCheckoutTemplateTransition } from '@/utils/checkoutTransitions';
import { FeatureFlag } from '@/constants/featureFlags';
import { useIsLandscape } from '@/hooks/useIsLandscape';
import { LandscapeRotateScreen } from '@/components/LandscapeRotateScreen/LandscapeRotateScreen';

interface TemplateRendererProps {
  statusOrderPolling: PollingStatus;
}

/**
 * Top-level dispatcher: resolves the active template from context and
 * renders its `View`. Replaces the legacy `if/else` chain in `CheckoutWrapper`.
 */
export const TemplateRenderer = ({ statusOrderPolling }: TemplateRendererProps) => {
  const { template, featureFlags } = useContext(CheckoutContext);
  const showLandscapeRotateScreen = Boolean(featureFlags?.[FeatureFlag.CHECKOUT_SHOW_LANDSCAPE_ROTATE]);
  const isLandscape = useIsLandscape();
  const previousTemplateTypeRef = useRef(template.type);
  const [templateTransitionClass, setTemplateTransitionClass] = useState<string>();
  const { View } = getCheckoutTemplate(template.type);

  useLayoutEffect(() => {
    const previousTemplateType = previousTemplateTypeRef.current;
    previousTemplateTypeRef.current = template.type;

    if (previousTemplateType === template.type) {
      return;
    }

    if (consumeSuppressNextCheckoutTemplateTransition()) {
      return;
    }

    const transitionClass =
      previousTemplateType === TemplateType.PREFERRED_PAYMENT_METHOD && template.type === TemplateType.DEFAULT
        ? 'checkout-template-transition checkout-template-transition--preferred-default'
        : 'checkout-template-transition';
    setTemplateTransitionClass(transitionClass);
    const timer = window.setTimeout(() => setTemplateTransitionClass(undefined), 220);
    return () => window.clearTimeout(timer);
  }, [template.type]);

  if (isLandscape && showLandscapeRotateScreen) {
    return <LandscapeRotateScreen />;
  }

  return (
    <div className={templateTransitionClass}>
      <View statusOrderPolling={statusOrderPolling} />
    </div>
  );
};
